import React from 'react';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme } from '@mui/material/styles';
import AudioPlayer from '../../components/AudioPlayer';
import { googleAiVoiceStyles } from '../GoogleAiVoiceStyle';
import { GoogleAiVoiceSegment, GoogleSpeakerSlot } from '../types';

interface GoogleScriptEditorPanelProps {
  multiSpeaker: boolean;
  segments: GoogleAiVoiceSegment[];
  singleSpeakerText: string;
  onSingleSpeakerTextChange: (value: string) => void;
  onUpdateSegment: (index: number, next: GoogleAiVoiceSegment) => void;
  onDeleteSegment: (index: number) => void;
  onAddSegment: () => void;
  audioUrl: string;
  audioDuration: number | null;
  disabled: boolean;
}

export default function GoogleScriptEditorPanel({
  multiSpeaker,
  segments,
  singleSpeakerText,
  onSingleSpeakerTextChange,
  onUpdateSegment,
  onDeleteSegment,
  onAddSegment,
  audioUrl,
  audioDuration,
  disabled,
}: GoogleScriptEditorPanelProps) {
  const theme = useTheme();

  return (
    <div style={googleAiVoiceStyles.editorColumn}>
      <div style={googleAiVoiceStyles.editorSurface}>
        {!multiSpeaker ? (
          <div style={googleAiVoiceStyles.singleSpeakerContainer(theme)}>
            <textarea
              style={googleAiVoiceStyles.singleSpeakerTextarea(theme)}
              placeholder='Write the text you wish to have generated into AI Voice'
              value={singleSpeakerText}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => onSingleSpeakerTextChange(event.target.value)}
              disabled={disabled}
            />
          </div>
        ) : (
          <div style={googleAiVoiceStyles.multiSpeakerContainer(theme)}>
            {segments.map((segment, index) => (
              <div key={segment.id} style={googleAiVoiceStyles.segmentCard(theme)}>
                <div style={googleAiVoiceStyles.segmentHeader}>
                  <Select
                    variant='standard'
                    size='small'
                    value={segment.speaker}
                    onChange={(event) => onUpdateSegment(index, { ...segment, speaker: event.target.value as GoogleSpeakerSlot })}
                    sx={{ minWidth: 170 }}
                    disabled={disabled}
                  >
                    <MenuItem value='A'>Voice #1</MenuItem>
                    <MenuItem value='B'>Voice #2</MenuItem>
                  </Select>
                  <IconButton size='small' onClick={() => onDeleteSegment(index)} disabled={segments.length <= 1 || disabled} aria-label='Delete section'>
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </div>
                <textarea
                  style={googleAiVoiceStyles.segmentTextarea(theme)}
                  placeholder='Write section text'
                  value={segment.text}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => onUpdateSegment(index, { ...segment, text: event.target.value })}
                  disabled={disabled}
                />
              </div>
            ))}
            <div style={googleAiVoiceStyles.addSegmentRow(theme)}>
              <IconButton onClick={onAddSegment} aria-label='Add section' disabled={disabled}>
                <AddCircleOutlineIcon />
              </IconButton>
            </div>
          </div>
        )}
      </div>

      <AudioPlayer audioSrc={audioUrl} handleSetVolume={() => void 0} duration={audioDuration} autoPlayOnSrcChange />
    </div>
  );
}
